import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  assignUserToReport,
  deleteUserFromReport,
  listAssignedUsers,
  listUsersWithoutSpecificReport,
  updateUserFromReport,
} from "../../../../apis";
import { useTranslation } from "react-i18next";
import SearchBar from "../../../SearchBar/Search";
import { MCButton, MCTooltip } from "../../../MainComponents";
import { enqueueSnackbar, useSnackbar } from "notistack";
import Icon from "../../../Icon/Icon";
import { ModalAssignUser } from "./ModalAssignUser";
import { ListItemButton, ListItemAvatar, ListItemText, Avatar, Chip, useTheme, alpha } from "@mui/material";
import { BoldProfileTick } from "../../../Icon";

import "./styles.scss";

const sessionEmail = JSON.parse(localStorage.getItem("Session"))?.email || "";

export const Users = ({ idReport }) => {
  const [t] = useTranslation("users");

  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [filteredInactiveUsers, setFilteredInactiveUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showModalAddExternalUser, setShowModalAddExternalUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 🔹 Cargar usuarios activos e inactivos
  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      listAssignedUsers(idReport),
      listUsersWithoutSpecificReport(idReport),
    ])
      .then(([assign, without]) => {
        setActiveUsers(assign.users);
        setInactiveUsers(without.users);
        setBlockedUsers(without.blocked_users || []);
      })
      .catch((err) => {
        console.log(err);
        setActiveUsers([]);
        setInactiveUsers([]);
        setBlockedUsers([]);
      })
      .finally(() => setIsLoading(false));
  }, [idReport]);

  // 🔹 Actualizar asignación de usuarios
  const handleChange = async (user, setIsLoading, target, rolesInvestigation, rolesResolution, details) => {
    setIsLoading(true);
    try {
      if (target === "active") {
        await deleteUserFromReport(user.id, idReport);
        const kickedUser = activeUsers.find((item) => item.id === user.id);
        setActiveUsers(activeUsers.filter((item) => item.id !== user.id));
        // El backend agrega el reporte a deny_assign, por lo que el usuario
        // pasa a la lista de bloqueados (no a los no asignados).
        setBlockedUsers([...blockedUsers, { ...kickedUser, assign_details: undefined }]);
        enqueueSnackbar("Usuario desactivado", { variant: "info" });
      }

      if (target === "inactive") {
        await assignUserToReport(user.id, idReport, rolesInvestigation, rolesResolution, details);
        const activatedUser = inactiveUsers.find((item) => item.id === user.id);
        setInactiveUsers(inactiveUsers.filter((item) => item.id !== user.id));
        setActiveUsers([...activeUsers, { ...activatedUser, assign_details: { rolesInvestigation, rolesResolution, details } }]);
        enqueueSnackbar("Usuario asignado correctamente", { variant: "success" });
      }

      if (target === "update") {
        await updateUserFromReport(user.id, { rolesInvestigation, rolesResolution, details }, idReport);
        setActiveUsers((prev) =>
          prev.map((item) =>
            item.id === user.id ? { ...item, assign_details: { rolesInvestigation, rolesResolution, details } } : item
          )
        );
        enqueueSnackbar("Usuario actualizado", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar("Error al procesar la solicitud", { variant: "error" });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Filtro de búsqueda
  const handleSearch = (value) => {
    setSearch(value);
    if (value.trim() === "") {
      setFilteredInactiveUsers([]);
      return;
    }
    const cleaned = value.trim().toLowerCase();
    const filtered = inactiveUsers.filter((u) => u.name.toLowerCase().includes(cleaned));
    setFilteredInactiveUsers(filtered);
  };

  // 🔹 Iniciales para avatar
  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((n) => n[0]?.toUpperCase())
      .join("")
      .slice(0, 2);

  // 🔹 Tags de roles de investigación (verde) y resolución (amarillo),
  //    con los mismos colores que los estatus process/finish.
  const RoleTags = ({ details }) => {
    const theme = useTheme();
    const investigation = details?.rolesInvestigation || [];
    const resolution = details?.rolesResolution || [];

    if (investigation.length === 0 && resolution.length === 0) return null;

    const renderTag = (value, color) => (
      <Chip
        key={value}
        label={t(`User.${value}`)}
        size="small"
        sx={{
          height: 18,
          borderRadius: "6px",
          fontSize: "0.65rem",
          fontWeight: 600,
          bgcolor: alpha(color, 0.15),
          color: color,
          border: `1px solid ${alpha(color, 0.35)}`,
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
    );

    return (
      <div className="d-flex flex-wrap gap-1 mt-1">
        {investigation.map((value) => renderTag(value, theme.palette.success.main))}
        {resolution.map((value) => renderTag(value, theme.palette.warning.main))}
      </div>
    );
  };

  // 🔹 Componente interno para cada usuario
  const UserListItem = ({ user, target }) => {
    const [showModal, setShowModal] = useState(false);
    const [isItemLoading, setIsItemLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // El usuario propio también puede abrir la modal y asignarse
    // responsabilidades; lo único vetado es desvincularse a sí mismo (eso lo
    // resuelve la modal con isSelf).
    const isSelf = user.email === sessionEmail;

    const handleClick = () => {
      setShowModal(true);
    };

    const getAvatarColor = (target) => {
      switch(target) {
        case "active":
          return "#02665c";
          case "inactive":
            return "#5c666e";
            case "blocked": 
            return "#bc342d";
            default:
              return "#EF9A9A";
      }
    }

    const roleChip = user.role_name && (
      <Chip
        label={user.role_name}
        size="small"
        sx={{
          height: 20,
          borderRadius: "6px",
          fontSize: "0.7rem",
          fontWeight: 600,
          letterSpacing: "0.2px",
          bgcolor: "rgba(99, 102, 241, 0.12)",
          color: "#4f46e5",
          border: "1px solid rgba(99, 102, 241, 0.25)",
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );

    return (
      <>
        <ListItemButton
          disabled={isItemLoading}
          onClick={handleClick}
          sx={{
            backgroundColor: user.email === sessionEmail ? "#e0f7fa" : "background.paper",
            borderRadius: "8px",
            mb: 1,
            border: "1px solid #ddd",
            cursor: "pointer",
          }}
        >
          <ListItemAvatar sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, pr: target !== "inactive" && user.role_name ? 1.5 : 0 }}>
            <Avatar sx={{ bgcolor: getAvatarColor(target), width: 40, height: 40, fontSize: 22 }}>
              {getInitials(user.name)}
            </Avatar>
            {target !== "inactive" && roleChip}
          </ListItemAvatar>

          <ListItemText
            primary={user.name}
            secondaryTypographyProps={{ component: "div" }}
            secondary={
              <>
                {user.email}
                {target === "active" && <RoleTags details={user.assign_details} />}
              </>
            }
          />

          {target === "inactive" && roleChip && (
            <span className="d-flex align-items-center ms-auto ps-2">{roleChip}</span>
          )}
        </ListItemButton>

        <ModalAssignUser
          user={user}
          handleChange={handleChange}
          setIsLoading={setIsItemLoading}
          show={showModal}
          target={target}
          isSelf={isSelf}
          onHide={() => setShowModal(false)}
        />
      </>
    );
  };

  return (
    <>
      <div className="my-3 p-4 dyTheme1 dyBorder1 rounded d-flex flex-column flex-md-row justify-content-between align-items-center">
        <h4 className="text-center mb-0">{t("Users.manage_users")}</h4>
      </div>

      <section className="my-3">
        <Row>
          {/* Usuarios inactivos */}
          <Col lg="6" className="p-0 pe-lg-2">
            <div className="dyTheme1 dyBorder1 rounded p-4 inactive-users">
              <div className="d-flex gap-2 mb-2">
                <Icon name="bold_user_minus" color={"purple"} />
                <h5>{t("Users.unassign_users")}</h5>
              </div>
              <div className="d-flex align-items-center">
                <SearchBar
                  placeholder={t("Users.enter_user")}
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <MCButton
                  variant="primary"
                  icon="outline_profile_add"
                  iconSize={24}
                  className="py-0 px-2"
                  size="sm"
                  onClick={() => setShowModalAddExternalUser(true)}
                />
              </div>
              <hr />
              <ul className="p-0" style={{ listStyle: "none" }}>
                {filteredInactiveUsers.length === 0 && search && <h5>{t("Users.no_results")}</h5>}
                {(filteredInactiveUsers.length > 0 ? filteredInactiveUsers : inactiveUsers).map((user) => (
                  <UserListItem key={user.id} user={user} target="inactive" />
                ))}
              </ul>
            </div>
          </Col>

          {/* Usuarios activos */}
          <Col lg="6" className="p-0 pe-lg-2">
            <div className="dyTheme1 dyBorder1 rounded p-4 active-users">
                <div className="d-flex gap-2 mb-2">
                  <Icon name="bold_user_tick" color={"green"} />
                  <h5>
                    {`${t("Users.assigned_user")}${activeUsers.length === 0 ? ":" : ` (${activeUsers.length})`}`}
                  </h5>
                </div>

                  <hr />
                  <div>
              {!isLoading ? (
                  <ul className="p-0" style={{ listStyle: "none" }}>
                    {activeUsers.map((user) => (
                      <UserListItem key={user.id} user={user} target="active" />
                    ))}
                  </ul>
              ) : (
                <h5 className="mb-0">Cargando usuarios...</h5>
              )}
            </div>
            </div>
          </Col>
        </Row>

        {/* Usuarios bloqueados */}
        <Row className="p-0 ps-lg-2 mt-3 mt-lg-0">
          <div className="dyTheme1 dyBorder1 rounded p-4 mt-3 blocked-users">
          <div className="d-flex gap-2 mb-2">

            <Icon name="bold_forbidden_2" color={"red"} />
            <h5>{t("Users.blocked_users")}</h5>
          </div>
            <hr />
            <ul className="d-flex flex-row flex-wrap gap-2 p-0">
              {blockedUsers.map((user) => (
                <UserListItem key={user.id} user={user} target="blocked" />
              ))}
            </ul>
          </div>
        </Row>
      </section>
    </>
  );
};
